import React from 'react'
import { Button, Container, Icon, Spacer, Text, Form, Modal, Image, Loader } from 'roadie-ui'
import Photo from 'components/form/Photo'
import { ModalOptions } from 'types/global'
import { FeatureFlag } from 'Optimizely'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'
import sampleAutoInsuranceImg from 'images/sample-auto-insurance.png'
import { NumberedList } from 'components/layout/NumberedList'
import { ExternalLink } from 'components/layout/links'
import ButtonLink from 'components/Button/ButtonLink'
import { useInsuranceSubmissionViewModel, useInsuranceFailureViewModel } from './hooks/useInsuranceSubmissionViewModel'
import { LocalizedRichText, LocalizedText } from 'localization'

const InsuranceFailureReasonAlert: React.FC<{ alertText: string }> = (props) => {
  return (
    <>
      <Spacer $height="2rem" />
      <Container.Grid $gridTemplateColumns="3.0rem 1fr" $alignItems="center">
        <Icon.Close $color="error-base" $width="2.4rem" $height="2.4rem" />
        <Container.Position $padding="0 0 0 0.5rem" $textAlign="start">
          <Text.Body2 $color="neutral-black">{props.alertText}</Text.Body2>
        </Container.Position>
      </Container.Grid>
    </>
  )
}

export const InsuranceInput: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  vehicle_id: number
  expiration_date: string | null
  status: string | null
  disabled?: boolean
}> = (props) => {
  const { status } = useInsurancePollingContext()

  const insurance_verified = props.status == 'accepted'
  const canAddInsurance = !props.disabled && !insurance_verified && status != 'polling'
  const handleAddInsurance = () => {
    props.setModalOptions?.({ modalOpen: true, modalType: 'add-insurance' })
  }

  const inputContent = (
    <Container.Grid $padding="2.0rem" $gridTemplateColumns="max-content 1fr max-content" $alignItems="center">
      <Container.Position $position="relative" $width="3rem" $height="3rem">
        <Container.Position $color="neutral-black" $position="absolute" $top="0" $left="0" $right="0">
          <Icon.ShieldCar />
        </Container.Position>
      </Container.Position>
      <Container.Grid $textAlign="left" $padding="0 2rem 0 2rem">
        <Text.Subtitle2 $fontWeight="500" $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_proof_insurance" />
        </Text.Subtitle2>
        <Spacer $height="0.7rem" />
        {insurance_verified ? (
          <Text.Caption>
            <LocalizedRichText
              translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_expiration_value1"
              params={{ value1: props.expiration_date }}
            />
          </Text.Caption>
        ) : status == 'polling' ? (
          <Text.Caption $color="primary-dark">
            <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_uploading_photo" />
          </Text.Caption>
        ) : props.status == 'rejected' ? (
          <Text.Caption $color="error-base">
            <LocalizedText translationKey="dweb_insurance_submission_unable_add_proof" />
          </Text.Caption>
        ) : (
          <Text.Caption>
            <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_add_photo_insurance_card" />
          </Text.Caption>
        )}
      </Container.Grid>
      {status == 'polling' ? (
        <Loader size="40px" color="primary-base" />
      ) : (
        !insurance_verified && (
          <Text.Body1
            $fontWeight="500"
            $color={props.disabled ? 'neutral-base' : 'primary-base'}
            $textTransform="uppercase"
          >
            <LocalizedText translationKey={props.status == 'rejected' ? 'dweb_common_retry' : 'dweb_common_add'} />
          </Text.Body1>
        )
      )}
    </Container.Grid>
  )

  return (
    <>
      {canAddInsurance ? (
        <Button.Tertiary
          type="button"
          onClick={handleAddInsurance}
          data-cy="add-insurance-button"
          $display="block"
          $width="100%"
          $textAlign="inherit"
          $textTransform="none"
          $letterSpacing="0"
        >
          {inputContent}
        </Button.Tertiary>
      ) : (
        inputContent
      )}
      {!insurance_verified && status != 'polling' && (
        <FlaggedInsuranceSubmissionModal
          modalOptions={props.modalOptions}
          setModalOptions={props.setModalOptions}
          vehicle_id={props.vehicle_id}
          fromFailureModal={false}
          openModalOnTryAgain={true}
        />
      )}
    </>
  )
}

export const InsuranceSubmissionModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  vehicle_id: number
  fromFailureModal: boolean
  openModalOnTryAgain: boolean | null
  useS3UploadFlow: boolean
}> = (props) => {
  const { isWebView, photoKey, insuranceCardReqs, handleSubmit, handleChange, handlePhotoError, resetPhotoForm } =
    useInsuranceSubmissionViewModel({
      vehicle_id: props.vehicle_id,
      setModalOptions: props.setModalOptions,
      fromFailureModal: props.fromFailureModal,
      openModalOnTryAgain: props.openModalOnTryAgain,
      useS3UploadFlow: props.useS3UploadFlow,
    })
  const photoOutputProps = props.useS3UploadFlow ? { outputMimeType: 'image/png', outputExtension: 'png' } : {}

  return (
    <Form.Basic id="insurance-form" onSubmit={handleSubmit}>
      <Photo
        id="insurance-photo"
        key={photoKey}
        onChange={handleChange}
        onError={handlePhotoError}
        maxWidth={3000}
        {...photoOutputProps}
      >
        {(src: string) => {
          return (
            <>
              {props.fromFailureModal &&
                (props.openModalOnTryAgain ? (
                  <Button.Div.Primary $borderWidth="0" $width="100%" data-cy="insurance-try-again-button">
                    <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_try_again" />
                  </Button.Div.Primary>
                ) : (
                  <ButtonLink
                    variant="primary"
                    type="button"
                    $width="100%"
                    to={`/account/driving-info/vehicle-info/${props.vehicle_id}`}
                    onClick={() => {
                      props.setModalOptions?.({ modalOpen: false, modalType: null })
                    }}
                    data-cy="insurance-try-again-link"
                  >
                    <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_try_again" />
                  </ButtonLink>
                ))}

              <Modal.Basic
                open={props.modalOptions?.modalOpen && props.modalOptions?.modalType == 'add-insurance'}
                width={isWebView ? '350px' : '500px'}
              >
                <Container.Base $maxHeight="90vh">
                  <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
                    <Button.Tertiary type="button" onClick={resetPhotoForm} data-cy="insurance-modal-close-icon">
                      <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
                    </Button.Tertiary>
                  </Container.Position>
                  <Spacer $height="1.4rem" />
                  <Form.Basic id="add-insurance-form">
                    <Text.H4 $fontWeight="700" $color="neutral-black">
                      <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_add_insurance_card" />
                    </Text.H4>
                    <Spacer $height="1.4rem" />
                    <Text.Body1 $color="neutral-black">
                      <LocalizedText translationKey="dweb_insurance_submission_photo_requirements_intro" />
                    </Text.Body1>
                    <Spacer $height="2.4rem" />
                    <img src={sampleAutoInsuranceImg} width="70%" />
                    <Spacer $height="2.4rem" />
                    <NumberedList listItems={insuranceCardReqs} />
                    <Spacer $height="2.4rem" />
                    <Text.Body2 $color="neutral-darker">
                      <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_screenshot_tip" />
                    </Text.Body2>
                    <Spacer $height="2.4rem" />
                    <Text.Caption $color="neutral-darker">
                      <LocalizedText translationKey="dweb_insurance_submission_acknowledgement" />
                    </Text.Caption>
                    <Spacer $height="2.4rem" />
                    <Photo
                      id="insurance-photo"
                      key={photoKey}
                      onChange={handleChange}
                      onError={handlePhotoError}
                      maxWidth={3000}
                      {...photoOutputProps}
                    >
                      {() => {
                        return (
                          <>
                            <Button.Div.Primary $borderWidth="0" $width="100%" data-cy="insurance-add-photo-button">
                              <Text.Body1 $fontWeight="500">
                                <LocalizedText translationKey="dweb_common_add" />
                              </Text.Body1>
                            </Button.Div.Primary>
                          </>
                        )
                      }}
                    </Photo>
                    <Spacer $height="1.4rem" />
                    <Button.Secondary
                      $borderWidth="0"
                      $width="100%"
                      type="button"
                      onClick={resetPhotoForm}
                      data-cy="insurance-modal-close-button"
                    >
                      <Text.Body1 $fontWeight="500">
                        <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_close" />
                      </Text.Body1>
                    </Button.Secondary>
                  </Form.Basic>
                </Container.Base>
              </Modal.Basic>

              <Modal.Basic
                open={props.modalOptions?.modalOpen && props.modalOptions?.modalType == 'insurance-card'}
                width={isWebView ? '350px' : '500px'}
              >
                <Container.Base $maxHeight="90vh">
                  <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
                    <Button.Tertiary type="button" onClick={resetPhotoForm} data-cy="insurance-card-close-icon">
                      <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
                    </Button.Tertiary>
                  </Container.Position>
                  <Spacer $height="1.4rem" />
                  <Form.Basic id="photo-and-bio-form">
                    <Text.H4 $fontWeight="700" $color="neutral-black">
                      <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_add_insurance_card" />
                    </Text.H4>
                    <Spacer $height="1.4rem" />
                    <Text.Body1>
                      <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_photo_quality_error" />
                    </Text.Body1>
                    <Spacer $height="1.4rem" />
                    <Photo
                      id=""
                      defaultSrc={src}
                      onChange={handleChange}
                      onError={handlePhotoError}
                      maxWidth={3000}
                      {...photoOutputProps}
                    >
                      {(src: string) => {
                        return (
                          <>
                            <Image $maxHeight="50vh" src={src} />
                            <Spacer $height="2.4rem" />
                            <Form.Submit data-cy="insurance-card-save-button">
                              <LocalizedText translationKey="dweb_common_save" />
                            </Form.Submit>
                            <Spacer $height="1.4rem" />
                            <Button.Div.Secondary $width="100%" data-cy="insurance-choose-new-photo-button">
                              <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_choose_new_photo" />
                            </Button.Div.Secondary>
                          </>
                        )
                      }}
                    </Photo>

                    <Spacer $height="1.4rem" />
                    <Button.Secondary
                      $borderWidth="0"
                      $width="100%"
                      type="button"
                      onClick={resetPhotoForm}
                      data-cy="insurance-card-close-button"
                    >
                      <LocalizedText translationKey="dweb_common_close" />
                    </Button.Secondary>
                  </Form.Basic>
                </Container.Base>
              </Modal.Basic>
            </>
          )
        }}
      </Photo>
    </Form.Basic>
  )
}

export const FlaggedInsuranceSubmissionModal: React.FC<
  Omit<React.ComponentProps<typeof InsuranceSubmissionModal>, 'useS3UploadFlow'>
> = (props) => (
  <FeatureFlag feature="drive_insurance_s3_upload_flow">
    {(enabled: boolean) => <InsuranceSubmissionModal {...props} useS3UploadFlow={enabled} />}
  </FeatureFlag>
)

export const InsuranceFailureModal: React.FC<{
  modalOptions: ModalOptions
  setModalOptions: React.Dispatch<React.SetStateAction<ModalOptions>>
}> = ({ modalOptions, setModalOptions }) => {
  const { isWebView, vehicleId, attemptCount, response, openModalOnTryAgain } =
    useInsuranceFailureViewModel(setModalOptions)

  return (
    <Modal.Basic
      open={modalOptions.modalOpen && modalOptions.modalType == 'insurance-failure'}
      width={isWebView ? '350px' : '500px'}
    >
      <Container.Grid $justifyItems="center" $maxHeight="90vh">
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary
            type="button"
            onClick={() => setModalOptions({ modalOpen: false, modalType: null })}
            data-cy="insurance-failure-close-icon"
          >
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black">
          <LocalizedText translationKey="dweb_insurance_submission_unable_add_proof" />
        </Text.H4>
        <Spacer $height="3rem" />
        <Text.Body2 $color="neutral-darker">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_detected_issues" />
        </Text.Body2>
        <Container.Grid $width={isWebView ? '75%' : '50%'}>
          {response?.ocr_failure && <InsuranceFailureReasonAlert alertText="Blurry or unclear photo" />}
          {response?.no_vin_found && !response?.duplicate_vin && !response?.plate_vin_mismatch && (
            <InsuranceFailureReasonAlert alertText="Vehicle not found" />
          )}
          {response?.name_failure && (
            <InsuranceFailureReasonAlert alertText="Driver name doesn't match insurance policy" />
          )}
          {response?.insurance_expired && <InsuranceFailureReasonAlert alertText="Expired or inactive policy" />}
          {response?.unsupported_category && <InsuranceFailureReasonAlert alertText="Unsupported vehicle category" />}
          {response?.duplicate_vin && (
            <InsuranceFailureReasonAlert alertText="Duplicate VIN found - vehicle already exists on profile" />
          )}
        </Container.Grid>
        {(response?.ocr_failure || response?.no_vin_found) && (
          <>
            <Spacer $height="3rem" />
            <Text.Body2 $color="neutral-darker">
              <LocalizedRichText
                translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_photo_tips_link"
                params={{
                  link1: (
                    <ExternalLink
                      href={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Uploading-Vehicle-Info-Adding-Multiple-Vehicles-to-Your-Driver-Profile`}
                    >
                      <LocalizedText translationKey="dweb_account_basic_info_registration_roadie_support" />
                    </ExternalLink>
                  ),
                }}
              />
            </Text.Body2>
          </>
        )}
        <FeatureFlag feature="drive_insurance_failure_contact_support_button">
          {(enabled: boolean) => {
            return (
              enabled &&
              attemptCount >= 3 && (
                <>
                  <Spacer $height="2.4rem" />
                  <Text.Body1 $color="neutral-black">
                    <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_insurance_submission_support_help" />
                  </Text.Body1>
                </>
              )
            )
          }}
        </FeatureFlag>
        <Spacer $height="2.4rem" />
        <Container.Base $width="100%">
          <FlaggedInsuranceSubmissionModal
            modalOptions={modalOptions}
            setModalOptions={setModalOptions}
            vehicle_id={vehicleId}
            fromFailureModal={true}
            openModalOnTryAgain={openModalOnTryAgain}
          />
        </Container.Base>
        <FeatureFlag feature="drive_insurance_failure_contact_support_button">
          {(enabled: boolean) => {
            return (
              enabled &&
              attemptCount >= 3 && (
                <>
                  <Spacer $height="1.4rem" />
                  <Button.A.Secondary
                    $borderWidth="1px"
                    $width="100%"
                    type="button"
                    href="https://support.roadie.com/support/s/vin-verification"
                    data-cy="insurance-contact-support-button"
                  >
                    <LocalizedText translationKey="dweb_common_contact_support" />
                  </Button.A.Secondary>
                </>
              )
            )
          }}
        </FeatureFlag>
        <Spacer $height="1.4rem" />
        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => setModalOptions({ modalOpen: false, modalType: null })}
          data-cy="insurance-failure-close-button"
        >
          <LocalizedText translationKey="dweb_common_close" />
        </Button.Secondary>
      </Container.Grid>
    </Modal.Basic>
  )
}
